import { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { changeTemporaryRiskScore } from '../../core/_requests'
import { IClientItem } from '../../core/_models'
import { convertUTCtoMSK } from '../../../../../utils'
import { RiskScoreAfterChange, RiskScorePreview } from './RiskScorePreview'
import {
  DURATION_UNIT_LABELS,
  DurationUnit,
  MAX_DURATION_MINUTES,
  MIN_DURATION_MINUTES,
  SIGNED_INT_INPUT_RE,
  UNSIGNED_INT_INPUT_RE,
  parseIntegerInput,
  remainingDuration,
  toMinutes,
} from '../../core/riskScore.consts'
import { riskScoreSaveErrors } from '../../core/riskScoreSaveErrors'

interface Props {
  show: boolean
  token: string
  /** Автоматический (антифродовый) балл */
  riskPoints: number
  /** Ручная часть целиком: бессрочная плюс временная */
  manualChange: number
  /** Текущая временная корректировка (null — её нет) */
  pendingChange: number | null
  expiresAt: string | null
  currentReason: string | null
  onClose: () => void
  onSave: (client: IClientItem) => void
}

/**
 * Временная корректировка. Срок обязателен всегда: бессрочная правка делается другой формой.
 *
 * Форма правит АБСОЛЮТНОЕ значение — сохранение заменяет прежнюю корректировку целиком и заново
 * заводит таймер. Поэтому при открытии поверх активной корректировки предзаполняются ВСЕ поля:
 * значение, остаток срока и причина. Пустые поля при непустой настройке читались бы как «сейчас
 * ничего не задано» — ровно та претензия QA, из-за которой форму и разделили.
 */
export const ChangeTemporaryRiskScoreModal = ({
  show,
  token,
  riskPoints,
  manualChange,
  pendingChange,
  expiresAt,
  currentReason,
  onClose,
  onSave,
}: Props) => {
  const [value, setValue] = useState<string>('')
  const [durationValue, setDurationValue] = useState<string>('')
  const [durationUnit, setDurationUnit] = useState<DurationUnit>('hours')
  const [reason, setReason] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!show) {
      return
    }

    setValue(pendingChange === null ? '' : String(pendingChange))
    setReason(currentReason ?? '')
    setErrors({})

    if (expiresAt === null) {
      setDurationValue('')
      setDurationUnit('hours')

      return
    }

    // Показываем остаток, а не исходную длительность: её нигде не храним, да и в карточке
    // оператор видит именно срок «до», от которого и считает.
    const remaining = remainingDuration(expiresAt)
    setDurationValue(String(remaining.value))
    setDurationUnit(remaining.unit)
  }, [show, pendingChange, expiresAt, currentReason])

  const parsedValue = parseIntegerInput(value)
  const isValueZero = parsedValue === 0
  const isValueValid = parsedValue !== null && !isValueZero

  const parsedDuration = parseIntegerInput(durationValue)
  const durationMinutes = parsedDuration === null ? null : toMinutes(parsedDuration, durationUnit)
  const isDurationOutOfRange =
    durationMinutes !== null &&
    (durationMinutes < MIN_DURATION_MINUTES || durationMinutes > MAX_DURATION_MINUTES)
  const isDurationValid = durationMinutes !== null && !isDurationOutOfRange

  const nextManualChange = isValueValid ? manualChange - (pendingChange ?? 0) + parsedValue : null

  const handleSave = async () => {
    setIsSaving(true)
    setErrors({})

    try {
      const response = await changeTemporaryRiskScore(token, {
        value: parsedValue as number,
        reason,
        durationMinutes: durationMinutes as number,
      })

      if (response.data.success && response.data.externalUser) {
        onSave(response.data.externalUser)
        onClose()
      } else if (response.data.errors) {
        setErrors(response.data.errors as Record<string, string>)
      }
    } catch (e: unknown) {
      // The interceptor rejects with the response itself; this URL is out of its alerts, so the modal is the only message.
      setErrors(riskScoreSaveErrors(e))
    } finally {
      setIsSaving(false)
    }
  }

  const isFormComplete = isValueValid && isDurationValid && reason.trim().length > 0

  const maxHours = MAX_DURATION_MINUTES / 60

  return (
    <Modal
      show={show}
      onHide={() => { if (!isSaving) onClose() }}
      centered
      onEscapeKeyDown={(e) => { if (isSaving) e.preventDefault() }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Временная корректировка риск-балла</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.general_error && (
          <div className='alert alert-danger mb-5'>{errors.general_error}</div>
        )}

        <RiskScorePreview riskPoints={riskPoints} manualChange={manualChange} />

        <div className='fv-row mb-5'>
          <label className='required fs-6 fw-semibold mb-2'>Временная корректировка</label>
          <input
            type='text'
            inputMode='numeric'
            autoComplete='off'
            className={`form-control form-control-solid${errors.value || isValueZero ? ' is-invalid' : ''}`}
            placeholder='Например, -10'
            value={value}
            // Гейт не впускает недопустимый символ вместо того, чтобы вычищать его после ввода:
            // так каретка не прыгает. Минус разрешён — им снижают оценку.
            onChange={(e) => { if (SIGNED_INT_INPUT_RE.test(e.target.value)) setValue(e.target.value) }}
            disabled={isSaving}
          />
          <div className='form-text text-muted'>
            {pendingChange === null
              ? 'Целое число, отрицательное снижает оценку'
              : `Сейчас ${pendingChange > 0 ? '+' : ''}${pendingChange}` +
                (expiresAt ? ` до ${convertUTCtoMSK(expiresAt)}` : '') +
                '. Сохранение заменит корректировку целиком и заново заведёт таймер'}
          </div>
          {isValueZero && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>Ноль ничего не меняет — укажите ненулевое значение</div>
            </div>
          )}
          {errors.value && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>{errors.value}</div>
            </div>
          )}
        </div>

        <div className='fv-row mb-5'>
          <label className='required fs-6 fw-semibold mb-2'>Срок действия</label>
          <div className='d-flex gap-3'>
            <input
              type='text'
              inputMode='numeric'
              autoComplete='off'
              className={`form-control form-control-solid${errors.durationMinutes || isDurationOutOfRange ? ' is-invalid' : ''}`}
              placeholder='Например, 6'
              value={durationValue}
              // У срока знака быть не может, поэтому гейт строже: только цифры.
              onChange={(e) => { if (UNSIGNED_INT_INPUT_RE.test(e.target.value)) setDurationValue(e.target.value) }}
              disabled={isSaving}
            />
            <select
              className='form-select form-select-solid w-auto'
              value={durationUnit}
              onChange={(e) => setDurationUnit(e.target.value as DurationUnit)}
              disabled={isSaving}
            >
              <option value='minutes'>{DURATION_UNIT_LABELS.minutes}</option>
              <option value='hours'>{DURATION_UNIT_LABELS.hours}</option>
            </select>
          </div>
          <div className='form-text text-muted'>
            Отсчёт начнётся заново с момента сохранения. От 1 минуты до 30 суток
            ({maxHours} часов)
          </div>
          {isDurationOutOfRange && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                {durationMinutes !== null && durationMinutes < MIN_DURATION_MINUTES
                  ? 'Срок не может быть нулевым'
                  : `Максимум — 30 суток (${MAX_DURATION_MINUTES} минут)`}
              </div>
            </div>
          )}
          {errors.durationMinutes && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>{errors.durationMinutes}</div>
            </div>
          )}
        </div>

        <div className='fv-row mb-0'>
          <label className='required fs-6 fw-semibold mb-2'>Причина изменения</label>
          <input
            type='text'
            className={`form-control form-control-solid${errors.reason ? ' is-invalid' : ''}`}
            placeholder='Укажите причину'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSaving}
          />
          {errors.reason && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>{errors.reason}</div>
            </div>
          )}
        </div>

        <RiskScoreAfterChange
          riskPoints={riskPoints}
          manualChange={manualChange}
          nextManualChange={nextManualChange}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={isSaving}>
          Отмена
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={isSaving || !isFormComplete}>
          {isSaving ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              Сохранение...
            </>
          ) : (
            'Сохранить'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
